/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.style

import org.jetbrains.compose.web.css.CSSStyleRuleBuilder
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.GenericStyleSheetBuilder
import org.jetbrains.compose.web.css.backgroundColor
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.css.height
import org.jetbrains.compose.web.css.marginBottom
import org.jetbrains.compose.web.css.marginTop
import org.jetbrains.compose.web.css.maxHeight
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.minus
import org.jetbrains.compose.web.css.overflowY
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.paddingRight
import org.jetbrains.compose.web.css.pt
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.css.width
import systems.frictionless.impulse.browser.style.constants.AUTO
import systems.frictionless.impulse.browser.style.constants.P
import systems.frictionless.impulse.browser.style.constants.TEXTAREA
import systems.frictionless.impulse.browser.style.constants.UNSET
import systems.frictionless.impulse.browser.style.constants.colorGray
import systems.frictionless.impulse.browser.style.constants.colorLightGray
import systems.frictionless.impulse.browser.style.constants.colorWhite
import systems.frictionless.impulse.browser.style.constants.grid0_5
import systems.frictionless.impulse.browser.style.constants.grid10
import systems.frictionless.impulse.browser.style.constants.grid12
import systems.frictionless.impulse.browser.style.constants.grid16
import systems.frictionless.impulse.browser.style.constants.grid2
import systems.frictionless.impulse.browser.style.constants.grid4
import systems.frictionless.impulse.browser.style.constants.grid6
import systems.frictionless.impulse.browser.style.constants.grid7
import systems.frictionless.impulse.browser.style.constants.grid8
import systems.frictionless.impulse.browser.style.constants.grid9
import systems.frictionless.impulse.browser.style.support.Margin
import systems.frictionless.impulse.browser.style.support.Overflow
import systems.frictionless.impulse.browser.style.support.boxShadow
import systems.frictionless.impulse.browser.style.support.marginBlockEnd
import systems.frictionless.impulse.browser.style.support.marginHorizontal
import systems.frictionless.impulse.browser.style.support.marginVertical
import systems.frictionless.impulse.browser.style.support.paddingBottom
import systems.frictionless.impulse.browser.style.support.scrollbarWidth
import systems.frictionless.impulse.browser.style.support.webkitScrollbar
import systems.frictionless.impulse.browser.style.support.webkitScrollbarThumb
import systems.frictionless.impulse.browser.style.support.webkitScrollbarTrackPiece

@Suppress("LongMethod")
fun AppStyleSheet.whiteBoxDesign(styleSheetBuilder: GenericStyleSheetBuilder<CSSStyleRuleBuilder>) {
    styleSheetBuilder.apply {
        root style {
            AppStyleSheetVariables.fontSizeQuestionHintText(12.pt)
            AppStyleSheetVariables.fontSizeTextAreaPlaceholder(12.pt)
        }

        className(whiteBox) style {
            backgroundColor(colorWhite)
            borderRadius(grid2)
            padding(grid4)
            boxShadow("0 $grid0_5 $grid2 0 rgb(0 0 0 / 20%), 0 $grid4 $grid4 0 rgb(0 0 0 / 19%)")
            padding(grid8)
        }
        desc(className(intro), combine(type(P), className(noteBelowHeader))) style {
            marginBlockEnd(1.em)
        }
        combine(className(whiteBox), className(narrowBox)) style {
            maxWidth(400.px)
            marginHorizontal(Margin.AUTO)
        }
        desc(className(whiteBox), className(questionTextArea)) style {
            marginBottom(grid12)
        }
        className(insights) style {
            marginTop(grid7)
        }
        desc(className(insights), className(insightTexts)) style {
            height(100.vh - 480.px)
            overflowY(Overflow.SCROLL)
            paddingRight(grid6)
            scrollbarWidth(UNSET)
        }
        combine(className(whiteBox), className(fixedSize)) style {
            height(100.vh - 350.px)
            width(650.px)
            marginBottom(100.px)
            overflowY(AUTO)
            maxHeight(500.px)
            marginHorizontal(Margin.AUTO)
        }

        className(questionText) style {
            marginTop(0.px)
        }

        className(questionTextArea) style {
            height(grid10)
            marginTop(grid10)
        }

        className(questionMultiChoiceBox) style {
            marginVertical(grid9)
        }

        className(progress) style {
            display(DisplayStyle.None)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbar) style {
            display(DisplayStyle.Unset)
            width(grid2)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbarThumb) style {
            backgroundColor(colorGray)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbarTrackPiece) style {
            backgroundColor(colorLightGray)
        }

        desc(className(insights), className(whiteBox)) style {
            maxHeight(100.vh - 200.px)
        }
        desc(
            className(insights),
            combine(className(insightTexts), webkitScrollbar)
        ) style {
            display(DisplayStyle.Unset)
            width(grid2)
        }
        desc(
            className(insights),
            combine(className(insightTexts), webkitScrollbarThumb)
        ) style {
            backgroundColor(colorGray)
        }
        desc(
            className(insights),
            combine(className(insightTexts), webkitScrollbarTrackPiece)
        ) style {
            backgroundColor(colorLightGray)
        }
        desc(
            className(insightTexts),
            combine(className(insightsSection), lastChild)
        ) style {
            paddingBottom(UNSET)
        }

        className(questionText) style {
            marginTop(0.px)
        }

        className(questionTextArea) style {
            height(grid10)
            marginTop(grid10)
        }

        className(questionMultiChoiceBox) style {
            marginTop(grid9)
            marginBottom(grid9)
        }

        className(progress) style {
            display(DisplayStyle.None)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbar) style {
            display(DisplayStyle.Unset)
            width(grid2)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbarThumb) style {
            backgroundColor(colorGray)
        }

        combine(className(whiteBox), className(fixedSize), webkitScrollbarTrackPiece) style {
            backgroundColor(colorLightGray)
        }

        desc(className(questionTextArea), type(TEXTAREA)) style {
            height(grid16)
        }
    }
}
